import type { KeyboardEvent, RefObject } from "react";
import { useCallback } from "react";

import {
  CellDisplayType,
  getClearedValue,
  isExplicitActivationCell,
  resolveColumnType,
  resolveEffectiveType,
} from "src/cases/caseView/blocks/cellTypes";
import { EditableTableSelectionStore } from "src/cases/caseView/blocks/useEditableTableSelection";
import { TableBlockMultiColumnRecord } from "src/cases/queries/casesTypes";

type NavigationContext = {
  columnKeys: string[];
  editableColumnKeys: string[];
  editedData: TableBlockMultiColumnRecord;
  columnDisplayTypes: Record<string, CellDisplayType>;
  isNewRow: (rowIndex: number) => boolean;
  getTypeOverride: (
    rowIndex: number,
    columnKey: string,
  ) => CellDisplayType | undefined;
};

type UseEditableTableKeyboardOptions = {
  store: EditableTableSelectionStore;
  tableContainerRef: RefObject<HTMLDivElement | null>;
  /** Current table values, read through a ref. */
  latest: RefObject<NavigationContext>;
  updateCell: (rowIndex: number, columnKey: string, value: unknown) => void;
};

/** Keyboard navigation for the editable table. Cell editors own their
 *  editing keys and stop propagation before this handler runs. */
export const useEditableTableKeyboard = ({
  store,
  tableContainerRef,
  latest,
  updateCell,
}: UseEditableTableKeyboardOptions): ((e: KeyboardEvent) => void) => {
  // Blurring the open editor commits it before the selection moves on.
  const blurActiveEditor = useCallback(() => {
    if (
      document.activeElement instanceof HTMLElement &&
      document.activeElement !== tableContainerRef.current &&
      tableContainerRef.current?.contains(document.activeElement)
    ) {
      document.activeElement.blur();
    }
  }, []);

  return useCallback(
    (e: KeyboardEvent) => {
      const sel = store.getState().selectedCell;
      if (!sel) return;

      const { row, col } = sel;
      const keys = latest.current.columnKeys;
      const editableKeys = latest.current.editableColumnKeys;
      const dataLength = latest.current.editedData.length;
      const { selectCell, deselectCell, setEditing } = store.getState().actions;

      if (e.key === "Tab") {
        e.preventDefault();
        const tabKeys = latest.current.isNewRow(row) ? keys : editableKeys;
        if (tabKeys.length === 0) return;

        const currentEditableIndex = tabKeys.indexOf(col);

        if (e.shiftKey) {
          if (currentEditableIndex > 0) {
            blurActiveEditor();
            selectCell(row, tabKeys[currentEditableIndex - 1]);
          } else if (row > 0) {
            const prevRowKeys = latest.current.isNewRow(row - 1)
              ? keys
              : editableKeys;
            if (prevRowKeys.length > 0) {
              blurActiveEditor();
              selectCell(row - 1, prevRowKeys[prevRowKeys.length - 1]);
            }
          }
        } else {
          if (currentEditableIndex < tabKeys.length - 1) {
            blurActiveEditor();
            selectCell(row, tabKeys[currentEditableIndex + 1]);
          } else if (row < dataLength - 1) {
            const nextRowKeys = latest.current.isNewRow(row + 1)
              ? keys
              : editableKeys;
            if (nextRowKeys.length > 0) {
              blurActiveEditor();
              selectCell(row + 1, nextRowKeys[0]);
            }
          }
        }
        return;
      }

      if (
        e.target instanceof Node &&
        !tableContainerRef.current?.contains(e.target)
      ) {
        return;
      }

      const displayTypes = latest.current.columnDisplayTypes;
      const colIndex = keys.indexOf(col);
      const value = latest.current.editedData[row]?.[col];
      const resolvedDisplayType =
        latest.current.getTypeOverride(row, col) ??
        displayTypes[col] ??
        resolveColumnType(null, value);
      const cellType = resolveEffectiveType(value, resolvedDisplayType).type;

      if (sel.editing && cellType && isExplicitActivationCell(cellType)) return;

      switch (e.key) {
        case "Escape":
          e.preventDefault();
          deselectCell();
          break;

        case "Enter": {
          if (e.shiftKey) {
            e.preventDefault();
            if (row < dataLength - 1) {
              blurActiveEditor();
              selectCell(row + 1, col);
            } else {
              deselectCell();
            }
          } else if (cellType && isExplicitActivationCell(cellType)) {
            e.preventDefault();
            setEditing();
          }
          break;
        }

        case "Backspace":
        case "Delete": {
          const clearedValue = getClearedValue(cellType);
          if (clearedValue !== undefined) {
            e.preventDefault();
            updateCell(row, col, clearedValue);
          }
          break;
        }

        case "ArrowUp":
          e.preventDefault();
          if (row > 0) {
            blurActiveEditor();
            selectCell(row - 1, col);
          }
          break;

        case "ArrowDown":
          e.preventDefault();
          if (row < dataLength - 1) {
            blurActiveEditor();
            selectCell(row + 1, col);
          }
          break;

        case "ArrowLeft":
          e.preventDefault();
          if (colIndex > 0) {
            blurActiveEditor();
            selectCell(row, keys[colIndex - 1]);
          }
          break;

        case "ArrowRight":
          e.preventDefault();
          if (colIndex < keys.length - 1) {
            blurActiveEditor();
            selectCell(row, keys[colIndex + 1]);
          }
          break;
      }
    },
    [store, updateCell, blurActiveEditor],
  );
};
